import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as authModule from '@/services/auth';
import {
  signUp as authSignUp,
  signInWithPassword as authSignIn,
  signOut as authSignOut,
  getSession,
  getUser,
  onAuthStateChange,
  subjectOfAccessToken,
  type AuthUser,
  type AuthSession,
} from '@/services/auth';
import { fetchProfile as fetchProfileFromDb } from '@/services/profiles';
import { clearOnboardingCookie } from '@/components/onboarding/cookie';
import i18n from '@/i18n';
import type { Profile } from '@/types';

// ── Context types ──────────────────────────────────────────────────────────────

/**
 * The `user` field mirrors the shape previously provided by @supabase/auth-js
 * at the fields that are actually used across the codebase:
 *   user.id  → userId (UUID)
 *   user.email
 *
 * We keep `session` for any code that checks `session?.access_token`.
 */
export interface AuthContextUser {
  id: string;
  email: string;
}

interface AuthContextType {
  user: AuthContextUser | null;
  session: AuthSession | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    options?: {
      firstName?: string;
      lastName?: string;
      phone?: string;
      jobTitle?: string;
    },
  ) => Promise<{ error: Error | null }>;
  signIn: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<{ error: Error | null; needs2FA?: boolean; twoFactorToken?: string }>;
  completeTwoFactorLogin: (twoFactorToken: string, code: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ── Provider ───────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthContextUser | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const qc = useQueryClient();

  // Track whether a session restoration is in flight to avoid duplicate fetches
  const restoringRef = useRef(false);

  /**
   * The id of the last person this tab hydrated, kept so we can tell "my token
   * was rotated" from "somebody else is signed in now".
   *
   * Deliberately NOT cleared on sign-out. Sign out and sign a different person
   * in is the exact sequence that has to be caught, and it is only catchable if
   * the previous identity survives the gap between the two.
   */
  const lastUserIdRef = useRef<string | null>(null);

  /**
   * Everything React Query is holding belongs to whoever was signed in when it
   * was fetched, and almost none of the keys say whose it is: `credit-balance`,
   * `dashboard-summary`, `campaigns`, `onboarding-state`. Sign one person out
   * and another in, and the second person's first render is served the first
   * person's answers.
   *
   * That is not cosmetic. `onboarding-state` is what the route gate in
   * DashboardShell reads to decide whether the application may open at all, so
   * a brand new account inherited "this org exists", was let through the gate,
   * mounted the sidebar, and asked for a credit balance for an organisation it
   * does not have yet. That request is the 403 on the first screen after a
   * second sign-up, and the flash of dashboard chrome behind it.
   *
   * Called synchronously, before the new session is adopted, so the cache is
   * already empty by the time anything routes on it.
   */
  const adoptIdentity = useCallback((userId: string | null | undefined) => {
    if (!userId) return;
    if (lastUserIdRef.current && lastUserIdRef.current !== userId) {
      qc.clear();
    }
    lastUserIdRef.current = userId;
  }, [qc]);

  /**
   * Given a valid access token, fetch /auth/me to hydrate the user object.
   * Extracted into its own function so it can be reused on auth-state changes.
   */
  const hydrateUser = useCallback(async (accessToken: string) => {
    try {
      const authUser: AuthUser = await getUser(accessToken);
      const contextUser: AuthContextUser = { id: authUser.id, email: authUser.email };
      adoptIdentity(authUser.id);
      setUser(contextUser);
    } catch {
      // Token invalid — clear state
      setUser(null);
      setSession(null);
    }
  }, [adoptIdentity]);

  // ── Initial session restoration ───────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      if (restoringRef.current) return;
      restoringRef.current = true;

      try {
        const { data } = await getSession();
        if (cancelled) return;

        if (data.session?.accessToken) {
          setSession(data.session);
          await hydrateUser(data.session.accessToken);
        }
      } finally {
        if (!cancelled) setAuthReady(true);
        restoringRef.current = false;
      }
    }

    restoreSession();
    return () => { cancelled = true; };
  }, [hydrateUser]);

  // ── Auth state change listener ────────────────────────────────────────────

  useEffect(() => {
    const { data: { subscription } } = onAuthStateChange(async (event, authSession) => {
      if (event === 'SIGNED_IN' && authSession?.accessToken) {
        // Synchronous, and before the await. `signUp` fires this notification
        // from inside itself, and the screen that called it navigates the
        // moment it returns, so anything left until `hydrateUser` resolves
        // lands after the next route has already read the cache.
        //
        // The token is the fallback and it is the load-bearing half. A same-tab
        // sign-in carries a real user object; a CROSS-TAB one carries
        // `user: null` by construction, because the user object is deliberately
        // never persisted. So `authSession.user?.id` was undefined for every
        // cross-tab notification, `adoptIdentity` hit its own `if (!userId)
        // return`, and the clear was deferred to `hydrateUser` after a round
        // trip: exactly the asynchrony the docblock above says has been removed.
        adoptIdentity(authSession.user?.id ?? subjectOfAccessToken(authSession.accessToken));
        setSession(authSession);
        await hydrateUser(authSession.accessToken);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setSession(null);
      } else if (event === 'TOKEN_REFRESHED' && authSession?.accessToken) {
        // A rotation leaves the same person signed in, so there is deliberately
        // no /auth/me here: the user object has not changed.
        //
        // The identity check is still made. `adoptIdentity` is a no-op when the
        // subject matches, which is the whole meaning of this event, and the one
        // line of insurance is worth having because the classifier that produces
        // it reads a token it cannot always decode. Getting here with a
        // different subject would mean the cache still held the previous
        // person's tenant data with a new person's credentials in the store.
        adoptIdentity(subjectOfAccessToken(authSession.accessToken));
        setSession(authSession);
      }
    });

    return () => subscription.unsubscribe();
  }, [hydrateUser, adoptIdentity]);

  // ── Profile via React Query ───────────────────────────────────────────────

  const profileQuery = useQuery({
    queryKey: ['auth-profile', user?.id],
    queryFn: async () => {
      const profileData = await fetchProfileFromDb();
      return profileData as Profile;
    },
    enabled: !!user,
  });

  const profile = profileQuery.data ?? null;

  // Apply the stored language as soon as the profile lands.
  //
  // This lived in `useLanguage`, which is only mounted by the settings screens
  // — so a saved preference was applied only if the user happened to open
  // Settings. Everywhere else the app fell back to the detector and rendered
  // English, which made the preference look like it had never saved. It also
  // read `user.language`, and /auth/me does not return that field; only the
  // profile does.
  useEffect(() => {
    const lng = (profile as { language?: string } | null)?.language;
    if (lng && i18n.language !== lng) void i18n.changeLanguage(lng);
  }, [(profile as { language?: string } | null)?.language]);
  const loading = !authReady || (!!user && profileQuery.isLoading);

  // ── refreshProfile ────────────────────────────────────────────────────────

  const refreshProfile = useCallback(async () => {
    if (user) {
      await qc.invalidateQueries({ queryKey: ['auth-profile', user.id] });
    }
  }, [user, qc]);

  // ── signUp ────────────────────────────────────────────────────────────────

  const signUpHandler = useCallback(async (
    email: string,
    password: string,
    options?: { firstName?: string; lastName?: string; phone?: string; jobTitle?: string },
  ): Promise<{ error: Error | null }> => {
    const { error } = await authSignUp(email, password, {
      firstName: options?.firstName,
      lastName: options?.lastName,
      phone: options?.phone,
    });
    return { error };
  }, []);

  // ── signIn ────────────────────────────────────────────────────────────────

  const signInHandler = useCallback(async (
    email: string,
    password: string,
    rememberMe: boolean = true,
  ): Promise<{ error: Error | null; needs2FA?: boolean; twoFactorToken?: string }> => {
    const { data, error } = await authSignIn({ email, password });

    if (!error && data?.needs2FA) {
      return { error: null, needs2FA: true, twoFactorToken: data.twoFactorToken };
    }

    if (!error && data?.session?.accessToken) {
      // Hydrate user immediately so navigation doesn't race the event listener
      await hydrateUser(data.session.accessToken);
      setSession(data.session);
    }

    if (!error && !rememberMe && data?.session) {
      const handleUnload = () => {
        localStorage.removeItem('seedhub-auth');
      };
      window.addEventListener('beforeunload', handleUnload, { once: true });
    }

    return { error };
  }, [hydrateUser]);

  // ── completeTwoFactorLogin ───────────────────────────────────────────────

  const completeTwoFactorLoginHandler = useCallback(async (
    twoFactorToken: string,
    code: string,
  ): Promise<{ error: Error | null }> => {
    const { data, error } = await authModule.completeTwoFactorLogin(twoFactorToken, code);
    if (!error && data?.session?.accessToken) {
      await hydrateUser(data.session.accessToken);
      setSession(data.session);
    }
    return { error };
  }, [hydrateUser]);

  // ── signOut ───────────────────────────────────────────────────────────────

  const signOutHandler = useCallback(async () => {
    await authSignOut();
    setUser(null);
    setSession(null);
    qc.removeQueries({ queryKey: ['auth-profile'] });
    // The onboarding draft can hold 2FA backup codes, and it outlives the
    // session by seven days. The read side is already scoped to the user who
    // wrote it, so nobody else could get at them, but leaving the codes in a
    // cookie on a shared machine after their owner has signed out is not a
    // thing to rely on one check for. `clearOnboardingCookie` was written for
    // this and had no caller.
    clearOnboardingCookie();
  }, [qc]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        signUp: signUpHandler,
        signIn: signInHandler,
        completeTwoFactorLogin: completeTwoFactorLoginHandler,
        signOut: signOutHandler,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
